import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import OfferingCard from "@/components/OfferingCard";
import SEO from "@/components/SEO";

const PATH_TO_TYPE = { "/retreats": "retreat", "/trainings": "training", "/courses": "course", "/events": "event", "/offerings": "all", "/walk-with-me": "all" };

const SEO_COPY = {
  retreat:  { title: "Sacred Women's Yoga Retreats with Holly Johnson",                                 desc: "Intentional women's yoga retreats balancing aerial yoga, sound healing, sisterhood circles, and deep rest — held in Sedona, Wyoming, Colorado and curated destinations worldwide." },
  training: { title: "Yoga Teacher Trainings — Aerial, Restorative & SUP",                              desc: "Yoga Alliance certified teacher trainings from Aviana — 50hr aerial YACEP, 25hr restorative aerial, and SUP yoga. Held in-person + online with Holly Johnson, E-RYT 500." },
  course:   { title: "Online Yoga Courses — Self-Paced Teacher Trainings",                              desc: "Self-paced online yoga teacher trainings with downloadable manuals, lifetime video access, and 1:1 mentorship from Holly Johnson. Restorative Aerial Yoga · Yoga Alliance YACEP approved." },
  event:    { title: "Sister Circles, Sound Healing & Local Gatherings",                                desc: "Local women's gatherings near Sugar Grove, IL — Sister Circles, moon ceremonies, sound healing immersions, and the Bliss Bell Tent. Small, sacred, intentional." },
  all:      { title: "Walk With Me — Every Aviana Yoga Offering in One Place",                          desc: "Every upcoming Aviana Yoga offering — retreats, teacher trainings, online courses, sister circles, sound baths, and ceremonies — held with Holly Johnson. Sorted by date so you can choose what's calling you." },
};

const COPY = {
  retreat: {
    overline: "Sacred Retreats",
    title: "Travel the world. Return to yourself.",
    body: "Intentional itineraries balancing growth, wellness, play, and deep rest — held in beautiful corners of the world.",
  },
  training: {
    overline: "Teacher Trainings",
    title: "Unfurl your wings as a teacher.",
    body: "Carefully curated programs designed to set you up for success — aerial yoga, SUP yoga, and energy work.",
  },
  course: {
    overline: "Online Courses",
    title: "Study at your own pace.",
    body: "Self-paced learning with downloadable manuals, video lessons, and lifetime access to your library.",
  },
  event: {
    overline: "Sister Events",
    title: "Local sister gatherings.",
    body: "Sister Circles, moon ceremonies, sound healing, and Bliss Bell Tent gatherings near Sugar Grove, IL.",
  },
  all: {
    overline: "Walk with me",
    title: "Every gathering, in one place.",
    body: "Retreats, trainings, sister circles, sound baths, ceremonies — every upcoming offering held with Holly. Some open into their own dedicated landing pages; others register right from the card.",
  },
};

export default function Offerings() {
  const loc = useLocation();
  const type = PATH_TO_TYPE[loc.pathname] || "retreat";
  const [items, setItems] = useState([]);
  const c = COPY[type] || COPY.retreat;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // type="all" → fetch every kind of offering and sort by start date
        if (type === "all") {
          const types = ["retreat", "training", "course", "event"];
          const responses = await Promise.all(types.map((t) => api.get(`/offerings?type=${t}`).catch(() => ({ data: [] }))));
          const merged = responses.flatMap((r) => Array.isArray(r.data) ? r.data : []);
          merged.sort((a, b) => (a.start_date || "9999").localeCompare(b.start_date || "9999"));
          if (!cancelled) setItems(merged);
        } else {
          const { data } = await api.get(`/offerings?type=${type}`);
          if (!cancelled) setItems(Array.isArray(data) ? data : []);
        }
      } catch (_) {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [type]);

  return (
    <div className="pt-32 pb-24" data-testid={`offerings-page-${type}`}>
      <SEO
        title={SEO_COPY[type]?.title || SEO_COPY.retreat.title}
        description={SEO_COPY[type]?.desc || SEO_COPY.retreat.desc}
        path={loc.pathname}
      />
      <div className="container-aviana">
        <div className="max-w-3xl mb-16">
          <p className="overline mb-4">{c.overline}</p>
          <h1 className="font-serif text-5xl md:text-7xl text-aviana-ink leading-[1.05]">{c.title}</h1>
          <p className="mt-6 text-aviana-muted leading-relaxed">{c.body}</p>
        </div>
        {items.length === 0 ? (
          <p className="text-aviana-muted" data-testid="no-offerings">More offerings coming soon. Check back beneath the next full moon.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {items.map((o, i) => <OfferingCard key={o.id} offering={o} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
